import { useState, FormEvent, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import ModalComponent from "components/modal";
import { IconEye, IconEyeOff, IconInfoCircle } from "@tabler/icons-react";
import { useSharedState } from "state";
import { addUser, changeUserData } from "api/user";
import Select from "react-select";

type SelectOption = {
  value: number;
  label: string;
};

type FormErrors = Record<string, boolean>;
type FormRecords = {
  name: string;
  department_id: number;
  position: number;
  role?: number;
  email: string;
  username: string;
  password: string;
  manager_id?: number;
  document_approval_access?: boolean;
  payment_approval_access?: boolean;
  budget_access?: boolean;
  location_ids: number[];
};

const REQUIRED_FIELDS = ["name", "department_id", "position", "email", "username", "location_ids"] as const;

const getInputErrorMessage = (fieldId: string) =>
  fieldId === "position" ? "Вы не выбрали должность" : "Введите корректное значение";

const ROLE_OPTIONS = [
  {
    value: 0,
    label: "Пользователь",
  },
  {
    value: 1,
    label: "Админ",
  },
  {
    value: 2,
    label: "Только чтение",
  },
];

export default function NewUser() {
  const {
    users,
    getUsersData,
    departments,
    getDepartmentsData,
    positions,
    getPositionsData,
    locations,
    getLocationsData,
    notify,
  } = useSharedState();
  const { userId } = useParams();

  const existingUser = useMemo(() => users.find((user) => user.user_id === (userId ? +userId : 0)), [users, userId]);

  const [formRecords, setFormRecords] = useState<FormRecords>({
    name: "",
    department_id: 0,
    position: 0,
    email: "",
    username: "",
    password: "",
    location_ids: [],
  });

  useEffect(() => {
    getUsersData();
    getDepartmentsData();
    getPositionsData();
    getLocationsData();
  }, []);

  useEffect(() => {
    if (existingUser) {
      setFormRecords({
        name: existingUser.name || "",
        department_id:
          departments.find((department) => department.department_id === existingUser.department_id)?.department_id || 0,
        position: positions.find((position) => position.position === existingUser.position)?.position_id || 0,
        role: existingUser.role === "admin" ? 1 : existingUser.role === "read-only" ? 2 : 0,
        email: existingUser.email,
        username: existingUser.username || "",
        password: existingUser.password || "",
        manager_id: existingUser.manager_id,
        document_approval_access: existingUser.document_approval_access,
        payment_approval_access: existingUser.payment_approval_access,
        budget_access: existingUser.budget_access,
        location_ids: existingUser.locations.map((loc) => loc.location_id),
      });
    }
  }, [existingUser]);

  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const [isFormLoading, setFormLoading] = useState(false);
  const [isFormSubmitted, setFormSubmitted] = useState(false);

  const userFields = [
    {
      id: "name",
      title: "Имя",
      type: "text",
      placeholder: "Имя",
      options: undefined,
      isMulti: undefined,
    },
    {
      id: "department_id",
      title: "Отдел",
      type: "select",
      placeholder: "Выберите отдел",
      options: departments
        .filter((pos) => pos.status)
        .map((pos) => ({
          label: pos.department,
          value: pos.department_id,
        })),

      isMulti: false,
    },
    {
      id: "position",
      title: "Должность",
      type: "select",
      placeholder: "Выберите должность",
      options: positions
        .filter(
          (pos) =>
            pos.status &&
            pos.position.toLocaleLowerCase().trim() !== "менеджер сотрудника" &&
            pos.position.toLocaleLowerCase().trim() !== "создатель"
        )
        .map((pos) => ({
          label: pos.position,
          value: pos.position_id,
        })),

      isMulti: false,
    },
    {
      id: "role",
      title: "Роль",
      type: "select",
      placeholder: "Выберите роль",
      options: ROLE_OPTIONS,
      isMulti: false,
    },
    {
      id: "email",
      title: "Эл. почта",
      type: "email",
      placeholder: "Эл. почта",
      options: undefined,
      isMulti: undefined,
    },
    {
      id: "username",
      title: "Логин",
      type: "text",
      placeholder: "Логин",
      options: undefined,
      isMulti: undefined,
    },
    {
      id: "password",
      title: "Пароль",
      type: "password",
      placeholder: "Пароль",
      options: undefined,
      isMulti: undefined,
    },
    {
      id: "manager_id",
      title: "Менеджер",
      type: "select",
      placeholder: "Выберите менеджера",
      options: users
        .filter((user) => user.status)
        .map((user) => ({
          label: user.name,
          value: user.user_id,
        })),
      isMulti: false,
    },
    {
      id: "location_ids",
      title: "Филиалы",
      type: "select",
      placeholder: "Выберите филиалы",
      options: locations.filter((loc) => loc.status).map((loc) => ({ label: loc.location, value: loc.location_id })),
      isMulti: true,
    },
    {
      id: "document_approval_access",
      title: "Доступ к утверждению договоров",
      type: "checkbox",
      placeholder: undefined,
      options: undefined,
      isMulti: undefined,
    },
    {
      id: "payment_approval_access",
      title: "Доступ к утверждению оплат",
      type: "checkbox",
      placeholder: undefined,
      options: undefined,
      isMulti: undefined,
    },
    {
      id: "budget_access",
      title: "Доступ к бюджетированию",
      type: "checkbox",
      placeholder: undefined,
      options: undefined,
      isMulti: undefined,
    },
  ] as const;

  const validateForm = () => {
    const newErrors: FormErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      const value = formRecords[field];
      if (Array.isArray(value) ? value.length === 0 : !value || !value?.toString().trim()) {
        newErrors[field] = true;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  type InputChangeEvent = React.ChangeEvent<HTMLInputElement>;
  type SelectChangeEvent = readonly SelectOption[] | null;
  type SingleSelectEvent = SelectOption | null;

  const handleInputChange = (e: InputChangeEvent | SelectChangeEvent | SingleSelectEvent, fieldId?: string) => {
    if (e && "target" in e) {
      // Handle input change
      const { name, value } = e.target;
      const checked = (e.target as HTMLInputElement).checked;

      setFormRecords((prev) => ({
        ...prev,
        [name]:
          name === "document_approval_access" || name === "payment_approval_access" || name === "budget_access"
            ? checked
            : value,
      }));
      setErrors((prev) => ({ ...prev, [name]: false }));
    } else {
      // Handle select change
      // @ts-ignore
      const value = Array.isArray(e) ? e.map((option) => option.value) : e?.value ?? null;

      setFormRecords((prev) => ({ ...prev, [fieldId!]: value }));
      setErrors((prev) => ({ ...prev, [fieldId!]: false }));
    }
  };

  const submitUser = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      notify("Заполните все поля", "danger");
      return;
    }

    setFormLoading(true);
    try {
      const userData = {
        ...formRecords,
        password: formRecords.password || undefined,
        department_id: formRecords?.department_id || undefined,
        position: positions.find((position) => position.position_id === formRecords.position)?.position || "",
        role: formRecords.role === 1 ? "admin" : formRecords.role === 2 ? "read-only" : "",
        manager_id: formRecords?.manager_id || undefined,
        location_ids: formRecords.location_ids,
        document_approval_access: formRecords.document_approval_access || false,
        payment_approval_access: formRecords.payment_approval_access || false,
        budget_access: formRecords.budget_access || false,
        status: userId ? existingUser?.status : true,
      };

      if (userId) {
        await changeUserData({ ...userData, user_id: +userId });
        notify("Пользователь обновлен", "success");
      } else {
        await addUser(userData);
        notify("Пользователь добавлен", "success");
      }

      setFormSubmitted(true);
      getUsersData();
    } catch (error: any) {
      let err = JSON.stringify(JSON.stringify(error?.response?.data?.detail));
      notify(err, "danger", `Ошибка при ${userId ? "обновлении" : "добавлении"} пользователя`);
    } finally {
      setFormLoading(false);
    }
  };

  const renderField = ({ id, title, type, placeholder, options, isMulti }: (typeof userFields)[number]) => {
    const Component = "input";

    return (
      <div className={"col-lg-4"} key={id}>
        <div className="mb-3">
          <div className="d-flex align-items-center gap-1">
            <label
              className={`form-label ${
                type === "checkbox" || id === "manager_id" || id === "password" ? "" : "required"
              }`}
            >
              {title}
            </label>

            {id === "password" && (
              <div
                title={"Пароль должен содержать не менее 4 символов, включая одну букву"}
                data-bs-toggle="tooltip"
                data-bs-placement="top"
              >
                <IconInfoCircle className="icon text-primary mb-2 cursor-pointer" />
              </div>
            )}
          </div>

          <div className={`mb-3 ${type === "password" && !errors[id] ? "input-group input-group-flat" : "input-icon"}`}>
            {type === "select" ? (
              <Select<SelectOption, typeof isMulti>
                classNamePrefix={"react-select"}
                name={id}
                placeholder={placeholder}
                value={
                  isMulti
                    ? (formRecords[id] as number[])
                        .map((value) => options?.find((opt) => opt.value === value))
                        .filter((opt): opt is SelectOption => opt !== undefined)
                    : options?.find((opt) => opt.value === Number(formRecords[id]))
                }
                onChange={(newValue) => handleInputChange(newValue, id)}
                isDisabled={isFormLoading}
                options={options}
                isMulti={isMulti}
                isSearchable
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            ) : (
              <Component
                type={type === "password" && showPassword ? "text" : type}
                className={`${type === "checkbox" ? "form-check-input h-5 w-5" : "form-control rounded-2xl"} ${
                  errors[id] ? "is-invalid" : ""
                }`}
                name={id}
                placeholder={placeholder}
                value={type === "checkbox" ? undefined : formRecords[id] || ""}
                checked={type === "checkbox" ? formRecords[id] : undefined}
                onChange={handleInputChange}
                disabled={isFormLoading}
              />
            )}
            {errors[id] && <div className="invalid-feedback">{getInputErrorMessage(id)}</div>}
            {isFormLoading ? (
              <span className="input-icon-addon">
                <div className="spinner-border spinner-border-sm text-secondary" role="status" />
              </span>
            ) : (
              type === "password" &&
              !errors[id] && (
                <span className="input-group-text">
                  <div className="link-secondary cursor-pointer" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <IconEyeOff className="icon" /> : <IconEye className="icon" />}
                  </div>
                </span>
              )
            )}
          </div>
        </div>
      </div>
    );
  };

  const newUserBody = useMemo(
    () => (
      <form id="userForm" className="row" onSubmit={submitUser}>
        {userFields.map(renderField)}
      </form>
    ),
    [renderField, submitUser]
  );

  const newUserFooter = useMemo(
    () => (
      <button type="submit" form="userForm" className="btn btn-primary d-flex gap-1" disabled={isFormLoading}>
        {userId ? "Обновить пользователя" : "Создать пользователя"}
        {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
      </button>
    ),
    [isFormLoading, userId]
  );

  return (
    <ModalComponent
      header={userId ? "Редактировать пользователя" : "Новый пользователь"}
      body={newUserBody}
      footer={newUserFooter}
      disableClose={isFormLoading}
      shouldModalClose={isFormSubmitted}
    />
  );
}
